<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.IdentificationUpload") }}
      </h1>
      <p>
        {{ $t("PageSubTitle.paragraph.identificationUploadInstructions") }}
      </p>
    </div>
    <form @submit.prevent>
      <FormSelect
        name="identityDocumentCountry"
        type="text"
        iconType="GlobeCircle"
        iconColor="#0072CA"
        :items="countryList"
        :isLoading="isFetchingData"
        :label="$t('FormSelect.label.identityDocumentCountry')"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        name="identityDocumentType"
        type="text"
        iconType="NameUserCard"
        iconColor="#E540FF"
        :defaultItem="identityDocumentType"
        :label="$t('FormSelect.label.documentType')"
        :items="documentTypeList"
        @dropdownValueUpdate="updateDropdown"
      />

      <IdCardForm
        v-if="form.identityDocumentType.toLowerCase() === 'id card'"
        @enableSubmitButton="(hasErrors) => (isButtonDisabled = hasErrors)"
      />

      <PassportForm
        v-if="form.identityDocumentType.toLowerCase() === 'passport'"
        @enableSubmitButton="(hasErrors) => (isButtonDisabled = hasErrors)"
      />

      <NationalDriversLicenceForm
        v-if="
          form.identityDocumentType.toLowerCase() === 'national drivers licence'
        "
        @enableSubmitButton="(hasErrors) => (isButtonDisabled = hasErrors)"
      />

      <ProofOfResidencyForm
        v-if="form.identityDocumentType.toLowerCase() === 'proof of residency'"
        @enableSubmitButton="(hasErrors) => (isButtonDisabled = hasErrors)"
      />

      <ButtonBase
        :isDisabled="isButtonDisabled || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Submit my identity
      </ButtonBase>
      <IdentityQueueNext />
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useIdentityStore } from "@/stores/forms/identityFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import {
  camelize,
  unCamelizeIntoNormalStr,
} from "@/composables/helpers/useStringHelpers.js";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import IdCardForm from "@/components/forms/Identity/IdentityUpload/IdCardForm.vue";
import PassportForm from "@/components/forms/Identity/IdentityUpload/PassportForm.vue";
import NationalDriversLicenceForm from "@/components/forms/Identity/IdentityUpload/NationalDriversLicenceForm.vue";
import ProofOfResidencyForm from "@/components/forms/Identity/IdentityUpload/ProofOfResidencyForm.vue";
import IdentityQueueStepCounter from "@/components/layout/progress/IdentityQueueStepCounter.vue";
import IdentityQueueNext from "@/components/layout/progress/IdentityQueueNext.vue";

const globalStore = useGlobalStore();
const identityStore = useIdentityStore();
const identityQueueStore = useIdentityQueueStore();
const { fetchCountries } = useGlobalCountriesActions();

const router = useRouter();
const route = useRoute();
const countryList = ref([]);
const isButtonDisabled = ref(true);
const isFetchingData = ref(true);
const documentTypeList = ["National Drivers Licence", "Passport", "ID Card"];
const documentSubTypeMap = new Map([
  ["National Drivers Licence", "driving_licence"],
  ["Passport", "passport"],
  ["ID Card", "national_identity_card"],
]);

const form = ref({
  identityDocumentType: "",
});
const { postRequest, multiPostRequests } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const updateRoute = (documentType) => {
  const documentRoute = camelize(documentType);

  router.push({
    path: `/identity/idUpload/${documentRoute}`,
  });
};

const handleFetchingCountries = async () => {
  const pluckCountryName = (array) => array.map((country) => country.name);
  const formattedCountries = await fetchCountries();

  countryList.value = pluckCountryName(formattedCountries);
  isFetchingData.value = false;
};

onMounted(async () => {
  await handleFetchingCountries();

  const identityRouteParams = unCamelizeIntoNormalStr(route.params.id);

  form.value.identityDocumentType =
    identityRouteParams && documentTypeList.includes(identityRouteParams)
      ? identityRouteParams
      : documentTypeList[0];
});

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];

  if (field === "identityDocumentType") {
    updateRoute(value);
    form.value.identityDocumentType = value;
    isButtonDisabled.value = true;
  }
  identityStore.updateValue(field, value);
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const promises = [];

  globalStore.setIsFormLoading(true);

  Object.keys(identityStore.form).forEach((field) => {
    if (
      !identityStore.form[field].value ||
      typeof identityStore.form[field].value !== "object"
    ) {
      return;
    }
    const formData = new FormData();

    if (field === "driversLicenceBack") {
      formData.set("documentSide", "back");
    }

    if (field === "driversLicenceFront" || field === "idCard") {
      formData.set("documentSide", "front");
    }

    formData.set("documentFile", identityStore.form[field].value);
    formData.set("documentType", "document");
    formData.set("documentReference", custId);
    formData.set(
      "documentSubType",
      documentSubTypeMap.get(form.value.identityDocumentType)
    );

    promises.push(postRequest("documents", formData));
  });

  multiPostRequests(promises,  () => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  });
};
</script>
<!-- <style lang="scss" scoped>
.primary {
  color: var(--primary);
}
</style> -->
